import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ShieldCheck, Mail, Smartphone, ChevronDown, Clock, BookOpen, Zap } from 'lucide-react';
import { initMetaPixel, trackPageView, trackViewContent, trackCheckoutClick } from './utils/metaPixel';
import { UpsellModal } from './components/UpsellModal';
const BUY_LINK = 'https://impulsodigital.vip/cart/44687883272278:1';
// Intercept every buy-link click: prevent navigation, fire pixel,
// and broadcast an event so the upsell modal can open.
const handleCheckoutClick = (e?: React.MouseEvent) => {
  if (e) e.preventDefault();
  trackCheckoutClick();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('mp:open-upsell'));
  }
};
const CTAButton = ({
  text = 'Quiero mis recetas',
  className = ''
}) => <a href={BUY_LINK} onClick={handleCheckoutClick} className={`inline-block w-full md:w-auto bg-[#16a34a] hover:bg-[#15803d] text-white font-semibold text-lg py-4 px-8 rounded-xl transition-colors text-center shadow-sm ${className}`} data-id="element-0">
    {text}
  </a>;
const Eyebrow = ({
  text
}: {
  text: string;
}) => <p className="text-xs font-bold tracking-widest text-gray-500 uppercase mb-4" data-id="element-1">
    {text}
  </p>;
const StickyMobileCTA = () => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 400) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  if (!isVisible) return null;
  return <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-md border-t border-gray-200 p-4 z-50 md:hidden shadow-[0_-4px_20px_rgba(0,0,0,0.05)] transition-transform duration-300" data-id="element-2">
      <div className="flex items-center justify-between max-w-md mx-auto gap-4" data-id="element-3">
        <div className="flex flex-col" data-id="element-4">
          <span className="text-[#C2410C] font-bold text-lg leading-none" data-id="element-5">
            $12.990
          </span>
          <span className="text-xs text-gray-500 font-medium mt-1" data-id="element-6">
            +1000 recetas
          </span>
        </div>
        <a href={BUY_LINK} onClick={handleCheckoutClick} className="bg-[#16a34a] hover:bg-[#15803d] text-white font-semibold text-sm py-3 px-6 rounded-xl transition-colors text-center shadow-sm flex-1" data-id="element-7">
          Quiero cocinar así
        </a>
      </div>
    </div>;
};
export function App() {
  const [isUpsellOpen, setIsUpsellOpen] = useState(false);
  useEffect(() => {
    initMetaPixel();
    trackPageView();
    trackViewContent();
  }, []);
  useEffect(() => {
    const openHandler = () => setIsUpsellOpen(true);
    window.addEventListener('mp:open-upsell', openHandler);
    return () => window.removeEventListener('mp:open-upsell', openHandler);
  }, []);
  return <div className="min-h-screen bg-white font-sans text-[#111111] selection:bg-[#FAF7F2] selection:text-[#C2410C] pb-24 md:pb-0" data-id="element-8">
      {/* HERO — image above price */}
      <section className="pt-6 md:pt-10 pb-6 md:pb-10 px-4" data-id="element-9">
        <div className="max-w-3xl mx-auto text-center" data-id="element-10">
          <Eyebrow text="PACK COMPLETO · +1000 RECETAS · AIR FRYER" data-id="element-11" />
          <h1 className="font-serif text-4xl md:text-6xl font-semibold leading-[1.1] mb-6 text-[#111111] tracking-tight" data-id="element-12">
            Cocinar rico todos los días, sin pensarlo dos veces.
          </h1>
          <p className="text-lg md:text-xl text-[#4b4b4b] mb-8 leading-relaxed max-w-2xl mx-auto" data-id="element-13">
            El recetario con más de 1000 recetas para tu air fryer{' '}
            <span className="text-[#111111] font-medium" data-id="element-14">
              + 2 libros de regalo
            </span>{' '}
            para que nunca más te quedes sin ideas.
          </p>

          {/* Product image — full bundle (main ebook + 2 bonuses) */}
          <div className="relative mb-5" data-id="element-15">
            <motion.img src="https://cdn.magicpatterns.com/uploads/ooxppy7Jbp1ksTKQ3bLrKK/ChatGPT_Image_5_jun_2026,_23_57_15.png" alt="Pack +1000 Recetas Air Fryer con 2 libros de regalo" className="w-full max-w-[540px] md:max-w-[640px] mx-auto drop-shadow-2xl" initial={{
            y: 0
          }} animate={{
            y: [0, -14, 0]
          }} transition={{
            duration: 4,
            ease: 'easeInOut',
            repeat: Infinity
          }} data-id="element-16" />
          </div>

          {/* What's included — value framing */}
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 mb-6 text-sm font-medium" data-id="element-17">
            <span className="text-[#111111]" data-id="element-18">Recetario +1000 recetas</span>
            <span className="text-[#C2410C]" data-id="element-19">+</span>
            <span className="text-[#111111]" data-id="element-20">Plan de 7 días</span>
            <span className="text-[#C2410C]" data-id="element-21">+</span>
            <span className="text-[#111111]" data-id="element-22">Guía air fryer</span>
          </div>

          {/* Price */}
          <div className="mb-6" data-id="element-23">
            <p className="text-xs text-gray-400 mb-1" data-id="element-24">
              Valor real del pack completo:{' '}
              <span className="line-through" data-id="element-25">$74.990</span>
            </p>
            <div className="flex items-baseline justify-center gap-2 sm:gap-3 mb-1 whitespace-nowrap" data-id="element-26">
              <span className="text-gray-400 line-through text-sm sm:text-lg" data-id="element-27">
                Antes $49.990
              </span>
              <span className="text-2xl sm:text-3xl font-bold text-[#C2410C]" data-id="element-28">
                Hoy $12.990 ARS
              </span>
            </div>
            <p className="text-sm text-gray-500" data-id="element-29">
              Llevás los 3 libros por menos de lo que gastás en una comida.
            </p>
          </div>

          <div className="mb-6 flex justify-center" data-id="element-30">
            <CTAButton data-id="element-31" />
          </div>

          <div className="flex flex-wrap gap-2 justify-center" data-id="element-32">
            {['Listas en minutos', 'Pensadas para air fryer', 'Sin complicaciones', 'Acceso inmediato'].map((pill, i) => <span key={i} className="bg-gray-50 text-gray-600 text-xs font-medium px-3 py-1.5 rounded-full border border-gray-100" data-id="element-33">
                {pill}
              </span>)}
          </div>
        </div>
      </section>

      {/* PROBLEMA COTIDIANO */}
      <section className="py-5 md:py-9 px-4 bg-white" data-id="element-34">
        <div className="max-w-4xl mx-auto" data-id="element-35">
          <div className="text-center mb-6" data-id="element-36">
            <Eyebrow text="TE SUENA?" data-id="element-37" />
            <h2 className="font-serif text-3xl md:text-5xl font-semibold mb-4 tracking-tight" data-id="element-38">
              Tenés la air fryer… y siempre cocinás lo mismo.
            </h2>
            <p className="text-xl text-[#4b4b4b]" data-id="element-39">
              No es falta de ganas. Es falta de ideas.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-40">
            {[{
            title: 'Quedarte sin ideas a las 7pm',
            desc: 'Abrís la heladera, mirás, cerrás. Y terminás pidiendo delivery.'
          }, {
            title: 'Comer siempre lo mismo',
            desc: 'Las mismas 5 recetas en loop, semana tras semana.'
          }, {
            title: 'Que la air fryer junte polvo',
            desc: 'La compraste con ilusión y la usás solo para papas fritas.'
          }, {
            title: 'Cocinar sin tiempo',
            desc: 'Llegás cansada y querés algo rico, rápido y sin complicaciones.'
          }].map((item, i) => <div key={i} className="p-8 rounded-2xl border border-[#EAEAEA] bg-gray-50/50 hover:bg-gray-50 transition-colors" data-id="element-41">
                <h3 className="font-semibold text-lg mb-2" data-id="element-42">{item.title}</h3>
                <p className="text-[#4b4b4b] leading-relaxed" data-id="element-43">{item.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* LA SOLUCIÓN */}
      <section className="py-5 md:py-9 px-4 bg-[#FAF7F2]" data-id="element-44">
        <div className="max-w-5xl mx-auto" data-id="element-45">
          <Eyebrow text="LA SOLUCIÓN" data-id="element-46" />
          <div className="max-w-2xl mb-6 text-center md:text-left" data-id="element-47">
            <h2 className="font-serif text-3xl md:text-5xl font-semibold mb-4 tracking-tight leading-tight" data-id="element-48">
              Una biblioteca de recetas pensada para tu air fryer.
            </h2>
            <p className="text-lg text-[#4b4b4b] leading-relaxed" data-id="element-49">
              Reunimos las mejores preparaciones en un solo lugar. Olvidate de
              buscar en internet o pensar qué hacer con lo que tenés en la
              heladera. Simplemente abrís el recetario, elegís y cocinás.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 pt-8 border-t border-gray-200" data-id="element-50">
            <div data-id="element-51">
              <BookOpen className="text-[#C2410C] mb-4" size={24} strokeWidth={1.5} data-id="element-52" />
              <h3 className="font-semibold mb-2" data-id="element-53">+1000 recetas</h3>
              <p className="text-sm text-[#4b4b4b]" data-id="element-54">
                Desayunos, almuerzos, snacks, cenas y postres.
              </p>
            </div>
            <div data-id="element-55">
              <Zap className="text-[#C2410C] mb-4" size={24} strokeWidth={1.5} data-id="element-56" />
              <h3 className="font-semibold mb-2" data-id="element-57">Pensadas para air fryer</h3>
              <p className="text-sm text-[#4b4b4b]" data-id="element-58">
                Tiempos, temperaturas y trucos específicos.
              </p>
            </div>
            <div data-id="element-59">
              <Clock className="text-[#C2410C] mb-4" size={24} strokeWidth={1.5} data-id="element-60" />
              <h3 className="font-semibold mb-2" data-id="element-61">Listas en minutos</h3>
              <p className="text-sm text-[#4b4b4b]" data-id="element-62">
                Comidas reales con poco tiempo y poco esfuerzo.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFICIOS */}
      <section className="py-6 md:py-10 px-4 bg-white" data-id="element-63">
        <div className="max-w-4xl mx-auto" data-id="element-64">
          <div className="text-center mb-16" data-id="element-65">
            <Eyebrow text="POR QUÉ FUNCIONA" data-id="element-66" />
            <h2 className="font-serif text-3xl md:text-5xl font-semibold tracking-tight" data-id="element-67">
              Lo que cambia cuando tenés ideas en la mano.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-x-12 gap-y-6 mb-6" data-id="element-68">
            {['Tener ideas infinitas para cocinar', 'Aprovechar al máximo tu air fryer', 'Cocinar rápido aunque tengas poco tiempo', 'Variar las comidas todos los días', 'Reducir delivery y ultraprocesados', 'Comer un poco más liviano sin sentir que estás a dieta'].map((benefit, i) => <div key={i} className="flex items-start gap-3" data-id="element-69">
                <div className="w-1.5 h-1.5 rounded-full bg-[#16a34a] mt-2.5 flex-shrink-0" data-id="element-70"></div>
                <p className="text-lg text-[#111111]" data-id="element-71">{benefit}</p>
              </div>)}
          </div>

          <p className="text-sm text-gray-500 italic text-center max-w-2xl mx-auto leading-relaxed" data-id="element-72">
            Cuando disfrutás lo que comés y simplificás el día a día, comer
            mejor se vuelve sostenible — y bajar un poco de peso aparece como
            consecuencia, no como obsesión.
          </p>
        </div>
      </section>

      {/* FOOD GALLERY */}
      <section className="py-5 md:py-9 px-4 bg-[#FAF7F2]" data-id="element-73">
        <div className="max-w-6xl mx-auto text-center" data-id="element-74">
          <Eyebrow text="UN VISTAZO A LO QUE VAS A COCINAR" data-id="element-75" />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 mb-6" data-id="element-76">
            {['https://images.unsplash.com/photo-1628840042765-356cda07504e?w=900', 'https://images.unsplash.com/photo-1606756790138-261d2b21cd75?w=900', 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=900', 'https://images.unsplash.com/photo-1604908176997-125f25cc6f3d?w=900', 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=900', 'https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?w=900'].map((img, i) => <div key={i} className="overflow-hidden rounded-2xl aspect-square group" data-id="element-77">
                <img src={img} alt="Receta Air Fryer" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-78" />
              </div>)}
          </div>

          <p className="text-gray-500 text-sm" data-id="element-79">
            Y miles de ideas más, organizadas para cualquier momento del día.
          </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-5 md:py-9 px-4 bg-[#FAF7F2]" data-id="element-80">
        <div className="max-w-5xl mx-auto" data-id="element-81">
          <div className="text-center mb-8" data-id="element-82">
            <Eyebrow text="CÓMO FUNCIONA" data-id="element-83" />
            <h2 className="font-serif text-3xl md:text-4xl font-semibold tracking-tight" data-id="element-84">
              Tres pasos para empezar a cocinar hoy.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-85">
            <div className="hidden md:block absolute top-6 left-[15%] right-[15%] h-px bg-gray-200" data-id="element-86"></div>

            {[{
            num: '1',
            title: 'Comprás el ebook',
            desc: 'Pago único, acceso inmediato.'
          }, {
            num: '2',
            title: 'Lo recibís por email',
            desc: 'PDF instantáneo, abrilo en tu celular o compu.'
          }, {
            num: '3',
            title: 'Elegís y cocinás',
            desc: 'Más de 1000 recetas listas para empezar hoy mismo.'
          }].map((step, i) => <div key={i} className="relative text-center" data-id="element-87">
                <div className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center text-lg font-serif font-semibold mx-auto mb-6 relative z-10 text-[#C2410C]" data-id="element-88">
                  {step.num}
                </div>
                <h3 className="font-semibold text-lg mb-2" data-id="element-89">{step.title}</h3>
                <p className="text-[#4b4b4b] text-sm" data-id="element-90">{step.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* BONOS */}
      <section className="py-5 md:py-9 px-4 bg-[#FAF7F2]" data-id="element-91">
        <div className="max-w-5xl mx-auto" data-id="element-92">
          <div className="text-center mb-8" data-id="element-93">
            <Eyebrow text="ADEMÁS DEL RECETARIO PRINCIPAL" data-id="element-94" />
            <h2 className="font-serif text-2xl md:text-3xl font-semibold tracking-tight mb-3" data-id="element-95">
              Tu compra incluye 2 libros de regalo.
            </h2>
            <p className="text-[#4b4b4b] max-w-xl mx-auto" data-id="element-96">
              No son un relleno: son dos guías reales que te ayudan a arrancar
              hoy mismo y aprovechar tu air fryer al máximo.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-97">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 p-6 rounded-2xl border border-[#EAEAEA] bg-white" data-id="element-98">
              <img src="https://cdn.magicpatterns.com/uploads/d3h5dMDXGJxkWaWx6PFpCb/mock_l1.webp" alt="Plan de 7 días" className="w-32 h-32 object-cover rounded-xl shadow-sm" data-id="element-99" />
              <div className="text-center sm:text-left" data-id="element-100">
                <span className="inline-block bg-[#C2410C]/10 text-[#C2410C] text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider mb-2" data-id="element-101">
                  REGALO · VALOR $12.000
                </span>
                <h3 className="font-semibold mb-1" data-id="element-102">
                  Plan de comidas de 7 días
                </h3>
                <p className="text-sm text-[#4b4b4b]" data-id="element-103">
                  Una semana ya planificada para arrancar sin pensar.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 p-6 rounded-2xl border border-[#EAEAEA] bg-white" data-id="element-104">
              <img src="https://cdn.magicpatterns.com/uploads/aEpY4A61SAawmB1qipz1m2/mock_l2.webp" alt="Guía Air Fryer" className="w-32 h-32 object-cover rounded-xl shadow-sm" data-id="element-105" />
              <div className="text-center sm:text-left" data-id="element-106">
                <span className="inline-block bg-[#C2410C]/10 text-[#C2410C] text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider mb-2" data-id="element-107">
                  REGALO · VALOR $10.000
                </span>
                <h3 className="font-semibold mb-1" data-id="element-108">
                  Guía rápida de tu air fryer
                </h3>
                <p className="text-sm text-[#4b4b4b]" data-id="element-109">
                  Tiempos, temperaturas y trucos esenciales.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRUEBA SOCIAL */}
      <section className="py-5 md:py-9 px-4 bg-[#FAF7F2]" data-id="element-110">
        <div className="max-w-5xl mx-auto" data-id="element-111">
          <div className="text-center mb-8" data-id="element-112">
            <Eyebrow text="LO QUE DICEN" data-id="element-113" />
            <h2 className="font-serif text-3xl md:text-4xl font-semibold tracking-tight" data-id="element-114">
              Cocinas reales, gente real.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-115">
            {[{
            text: 'No pensé que iba a cocinar tanto en casa. Ahora la air fryer es mi mejor amiga.',
            author: 'Mariana',
            age: '34'
          }, {
            text: 'Por primera vez no me aburro de cocinar. Hay receta para todo.',
            author: 'Carla',
            age: '29'
          }, {
            text: 'Las explicaciones son clarísimas. Empecé sin saber nada de la air fryer.',
            author: 'Sofía',
            age: '41'
          }, {
            text: 'Probé como 20 recetas en un mes. Mi familia ni se da cuenta que estoy cocinando más sano.',
            author: 'Romina',
            age: '38'
          }, {
            text: 'Ahorré un montón en delivery. Ahora cocino rápido y rico.',
            author: 'Valeria',
            age: '45'
          }].map((testimonial, i) => <div key={i} className={`bg-white p-8 rounded-2xl border border-[#EAEAEA] shadow-sm ${i > 2 ? 'md:col-span-1.5 md:w-[calc(150%+12px)] md:justify-self-center' : ''} ${i === 3 ? 'md:col-start-1 md:ml-[50%]' : ''} ${i === 4 ? 'md:col-start-2 md:ml-[50%]' : ''}`} data-id="element-116">
                <div className="flex gap-1 text-yellow-400 mb-4" data-id="element-117">
                  {[...Array(5)].map((_, j) => <svg key={j} className="w-4 h-4 fill-current" viewBox="0 0 20 20" data-id="element-118">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-119" />
                    </svg>)}
                </div>
                <p className="text-[#4b4b4b] italic mb-6 leading-relaxed" data-id="element-120">
                  "{testimonial.text}"
                </p>
                <p className="font-semibold text-sm" data-id="element-121">
                  {testimonial.author}, {testimonial.age} años
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* OFERTA FINAL */}
      <section className="py-5 md:py-9 px-4 bg-white" data-id="element-122">
        <div className="max-w-3xl mx-auto text-center" data-id="element-123">
          <Eyebrow text="TU ACCESO HOY" data-id="element-124" />
          <h2 className="font-serif text-3xl md:text-5xl font-semibold tracking-tight mb-6" data-id="element-125">
            Todo en un solo lugar.
          </h2>

          <div className="max-w-[520px] mx-auto bg-white rounded-3xl border border-[#EAEAEA] shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 md:p-10 text-left" data-id="element-126">
            <div className="space-y-4 mb-8" data-id="element-127">
              <div className="flex justify-between items-center pb-4 border-b border-gray-100" data-id="element-128">
                <span className="font-medium" data-id="element-129">Recetario +1000 recetas</span>
                <span className="text-gray-400 line-through text-sm" data-id="element-130">
                  $49.990
                </span>
              </div>
              <div className="flex justify-between items-center pb-4 border-b border-gray-100" data-id="element-131">
                <span className="font-medium text-gray-600" data-id="element-132">
                  Bono 1 · Plan de 7 días
                </span>
                <span className="flex items-baseline gap-2" data-id="element-133">
                  <span className="text-gray-400 line-through text-xs" data-id="element-134">
                    $12.000
                  </span>
                  <span className="text-[#16a34a] font-semibold text-sm" data-id="element-135">
                    Gratis
                  </span>
                </span>
              </div>
              <div className="flex justify-between items-center pb-4 border-b border-gray-100" data-id="element-136">
                <span className="font-medium text-gray-600" data-id="element-137">
                  Bono 2 · Guía rápida
                </span>
                <span className="flex items-baseline gap-2" data-id="element-138">
                  <span className="text-gray-400 line-through text-xs" data-id="element-139">
                    $10.000
                  </span>
                  <span className="text-[#16a34a] font-semibold text-sm" data-id="element-140">
                    Gratis
                  </span>
                </span>
              </div>
            </div>

            <div className="flex justify-between items-center pb-4 mb-2 text-sm text-gray-500" data-id="element-141">
              <span data-id="element-142">Valor real del pack</span>
              <span className="line-through" data-id="element-143">$71.990</span>
            </div>

            <div className="flex justify-between items-end mb-2" data-id="element-144">
              <span className="text-lg font-semibold" data-id="element-145">Total hoy</span>
              <span className="text-4xl font-bold text-[#C2410C]" data-id="element-146">
                $12.990 ARS
              </span>
            </div>
            <p className="text-xs text-gray-500 text-right mb-8" data-id="element-147">
              Pago único · Acceso inmediato · Sin suscripción.
            </p>

            <CTAButton text="Quiero mis 1000 recetas" className="w-full mb-6" data-id="element-148" />

            <div className="flex justify-center gap-6 text-xs text-gray-500 font-medium" data-id="element-149">
              <div className="flex items-center gap-1.5" data-id="element-150">
                <ShieldCheck size={14} data-id="element-151" /> Compra segura
              </div>
              <div className="flex items-center gap-1.5" data-id="element-152">
                <Mail size={14} data-id="element-153" /> Acceso por email
              </div>
              <div className="flex items-center gap-1.5" data-id="element-154">
                <Smartphone size={14} data-id="element-155" /> Mobile y PC
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-5 md:py-9 px-4 bg-[#FAF7F2]" data-id="element-156">
        <div className="max-w-3xl mx-auto" data-id="element-157">
          <div className="text-center mb-16" data-id="element-158">
            <Eyebrow text="PREGUNTAS FRECUENTES" data-id="element-159" />
            <h2 className="font-serif text-3xl md:text-4xl font-semibold tracking-tight" data-id="element-160">
              ¿Tenés dudas? Las respondemos.
            </h2>
          </div>

          <div className="space-y-4" data-id="element-161">
            {[{
            q: '¿Es un libro físico o digital?',
            a: 'Es un libro 100% digital en formato PDF. Lo recibís en tu email inmediatamente después de la compra y podés leerlo en tu celular, tablet o computadora, o imprimirlo si preferís.'
          }, {
            q: '¿Sirve para cualquier marca de air fryer?',
            a: 'Sí, las recetas están pensadas para adaptarse a cualquier marca y tamaño de freidora de aire.'
          }, {
            q: '¿Tengo que pagar todos los meses?',
            a: 'No, es un pago único. Pagás una sola vez y tenés acceso al material para siempre.'
          }, {
            q: '¿Qué pasa si no sé cocinar?',
            a: '¡Es ideal para vos! Las recetas están explicadas paso a paso, de forma simple y sin términos complicados.'
          }, {
            q: '¿Son ingredientes fáciles de conseguir?',
            a: 'Totalmente. Usamos ingredientes cotidianos que conseguís en cualquier supermercado o verdulería de barrio.'
          }, {
            q: '¿Cómo recibo los bonos de regalo?',
            a: 'Los bonos se envían automáticamente junto con el ebook principal a tu correo electrónico.'
          }].map((faq, i) => <details key={i} className="group bg-white rounded-2xl border border-[#EAEAEA] overflow-hidden" data-id="element-162">
                <summary className="flex justify-between items-center font-semibold cursor-pointer list-none p-6" data-id="element-163">
                  <span data-id="element-164">{faq.q}</span>
                  <span className="transition group-open:rotate-180" data-id="element-165">
                    <ChevronDown size={20} className="text-gray-400" data-id="element-166" />
                  </span>
                </summary>
                <div className="text-[#4b4b4b] px-6 pb-6 leading-relaxed" data-id="element-167">
                  {faq.a}
                </div>
              </details>)}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 text-center border-t border-[#EAEAEA] bg-white" data-id="element-168">
        <p className="text-sm text-gray-400" data-id="element-169">
          © {new Date().getFullYear()} Recetas Air Fryer. Todos los derechos
          reservados.
        </p>
        <p className="text-xs text-gray-400 mt-2 max-w-xl mx-auto px-4" data-id="element-170">
          Este sitio no es parte del sitio web de Facebook o Facebook Inc.
          Además, este sitio NO está respaldado por Facebook de ninguna manera.
          FACEBOOK es una marca registrada de FACEBOOK, Inc.
        </p>
      </footer>

      <StickyMobileCTA data-id="element-171" />

      <UpsellModal isOpen={isUpsellOpen} onClose={() => setIsUpsellOpen(false)} data-id="element-172" />
    </div>;
}